import React from 'react';
import { agentPageLabel, findAgentCommand } from '../../services/agent/catalog';
import type { AgentPlan, AgentStep, AgentStepStatus } from '../../services/agent/executor';
import { useT } from '../../i18n';

// 操作计划卡片: 每一步的状态、参数、理由和结果; 写入/运行类步骤在这里人工确认。

const STATUS: Record<AgentStepStatus, { icon: string; color: string; label: string }> = {
  pending: { icon: '○', color: '#8598AD', label: '待执行' },
  awaiting: { icon: '◐', color: '#D4770C', label: '待确认' },
  running: { icon: '◌', color: '#2874A6', label: '执行中' },
  done: { icon: '✓', color: '#27864A', label: '完成' },
  failed: { icon: '✕', color: '#C0392B', label: '失败' },
  skipped: { icon: '–', color: '#8598AD', label: '已跳过' },
};

type Props = {
  plan: AgentPlan;
  onConfirm: (stepKey: string) => void;
  onSkip: (stepKey: string) => void;
  onStop: () => void;
};

export default function AgentPlanCard({ plan, onConfirm, onSkip, onStop }: Props) {
  const t = useT();

  const describeArgs = (step: AgentStep) => {
    const parts: string[] = [];
    if (typeof step.args.page === 'string') parts.push(t(agentPageLabel(step.args.page)));
    if (step.args.design_id !== undefined) parts.push(t('方案 #{id}', { id: String(step.args.design_id) }));
    if (Array.isArray(step.args.stages)) parts.push(t('第 {list} 段', { list: step.args.stages.join(', ') }));
    if (step.command === 'fracsim.select_stages' && !Array.isArray(step.args.stages)) parts.push(t('全部段'));
    if (typeof step.args.brief === 'string' && step.args.brief) parts.push(`“${step.args.brief}”`);
    return parts.join(' · ');
  };

  const active = !plan.stopped && plan.steps.some((s) => ['pending', 'awaiting', 'running'].includes(s.status));

  return (
    <div style={{ alignSelf: 'stretch', flexShrink: 0, border: '1px solid #D6DAE0', borderRadius: 8, background: '#FFFFFF', overflow: 'hidden' }}>
      <div style={{
        padding: '6px 10px', fontSize: 11, fontWeight: 700, color: '#4F6680', background: '#F4F6F9',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span>{plan.source === 'template' ? t('命令模板 · 操作计划') : t('爱压 AI · 操作计划')}</span>
        {active ? (
          <button type="button" onClick={onStop} style={smallButton('#C0392B', '#fff', '#E3B6B0')}>{t('停止')}</button>
        ) : plan.stopped ? <span style={{ color: '#8598AD', fontWeight: 400 }}>{t('已停止')}</span> : null}
      </div>
      {plan.steps.map((step, index) => {
        const spec = findAgentCommand(step.command);
        const st = STATUS[step.status];
        const args = describeArgs(step);
        return (
          <div key={step.key} style={{ padding: '7px 10px', borderTop: index ? '1px solid #ECEEF1' : 'none', fontSize: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ color: st.color, fontWeight: 700, width: 12, textAlign: 'center' }}>{st.icon}</span>
              <span style={{ flex: 1, minWidth: 0, color: '#1E2A3A', fontWeight: 600 }}>
                {index + 1}. {t(spec?.title ?? step.command)}
                {args && <span style={{ color: '#4F6680', fontWeight: 400 }}> · {args}</span>}
              </span>
              <span style={{ color: st.color, fontSize: 10.5, whiteSpace: 'nowrap' }}>{t(st.label)}</span>
            </div>
            {step.reason && <div style={{ marginLeft: 18, color: '#8598AD', fontSize: 11, marginTop: 2 }}>{step.reason}</div>}
            {step.result && (
              <div style={{
                marginLeft: 18, marginTop: 3, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                color: step.status === 'failed' ? '#C0392B' : '#4F6680',
              }}>
                {step.result}
              </div>
            )}
            {step.status === 'awaiting' && (
              <div style={{ marginLeft: 18, marginTop: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
                <button type="button" onClick={() => onConfirm(step.key)} style={smallButton('#fff', '#6A4C93', '#6A4C93')}>
                  {spec?.kind === 'run' ? t('确认运行') : t('确认执行')}
                </button>
                <button type="button" onClick={() => onSkip(step.key)} style={smallButton('#4F6680', '#fff', '#D6DAE0')}>{t('跳过')}</button>
                <span style={{ fontSize: 10.5, color: '#8598AD' }}>
                  {spec?.kind === 'run' ? t('将启动求解器, 可在页面上随时取消') : t('会修改方案或打开确认对话框')}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const smallButton = (color: string, background: string, border: string): React.CSSProperties => ({
  padding: '2px 10px', fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: 'pointer',
  color, background, border: `1px solid ${border}`, whiteSpace: 'nowrap',
});
